<script setup lang="ts">
import { InfoTip } from '@it-tools/shell'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  LOAD_CATEGORIES,
  type LoadEstimate,
  PSU_SIZES,
  type PsuRecommendation,
  TARGET_LOAD,
} from '../model/power'

const props = defineProps<{
  load: LoadEstimate | null
  psu: PsuRecommendation | null
}>()

const { t, n } = useI18n()

const watts = (value: number): string =>
  t('form.watts', { value: n(value, { maximumFractionDigits: 0 }) })
const percent = (value: number): string => n(value, { style: 'percent', maximumFractionDigits: 0 })

// Bars are relative to the peak, so the biggest consumer is obvious at a glance.
const breakdown = computed(() => {
  const load = props.load
  if (!load) return []
  return LOAD_CATEGORIES.filter((category) => load.parts[category] > 0).map((category) => ({
    category,
    watts: load.parts[category],
    share: load.parts[category] / load.peakWatts,
  }))
})

// An overloaded build is drawn full, not off the end of the track.
const meterWidth = computed(() => `${Math.min(props.psu?.loadShare ?? 0, 1) * 100}%`)
const targetLeft = `${TARGET_LOAD * 100}%`
const largestSize = Math.max(...PSU_SIZES)
</script>

<template>
  <section class="panel" aria-labelledby="psu-heading">
    <h2 id="psu-heading" class="panel__title">{{ t('results.heading') }}</h2>

    <template v-if="load && psu">
      <p class="panel__headline" data-testid="recommended">
        <!-- Leading spaces keep the text "650 W recommended": the template drops
             whitespace between tags, and flex layout ignores it anyway. -->
        <span class="panel__value mono">{{ n(psu.watts) }}</span>
        <span class="panel__unit">{{ ` ${t('form.wattsUnit')}` }}</span>
        <span class="panel__value-label">{{ ` ${t('results.recommended')}` }}</span>
      </p>

      <div class="meter">
        <div class="meter__track" aria-hidden="true">
          <div class="meter__fill" :style="{ width: meterWidth }"></div>
          <div class="meter__target" :style="{ left: targetLeft }"></div>
        </div>
        <p class="meter__caption">
          {{
            t('results.loadShare', { load: watts(load.peakWatts), share: percent(psu.loadShare) })
          }}
          <InfoTip
            :label="t('results.loadShareLabel')"
            :text="t('results.loadShareHelp', { target: percent(TARGET_LOAD) })"
          />
        </p>
      </div>

      <p v-if="!psu.standard" class="panel__warning" role="status">
        {{ t('results.beyondStandard', { largest: watts(largestSize) }) }}
      </p>

      <h3 class="panel__subtitle">
        {{ t('results.breakdown') }}
        <InfoTip :label="t('results.breakdown')" :text="t('results.breakdownHelp')" />
      </h3>
      <ul class="breakdown">
        <li v-for="row in breakdown" :key="row.category" class="breakdown__row">
          <span class="breakdown__label">{{ t(`category.${row.category}`) }}</span>
          <span class="breakdown__watts mono">{{ watts(row.watts) }}</span>
          <span class="breakdown__bar" aria-hidden="true">
            <span class="breakdown__fill" :style="{ width: `${row.share * 100}%` }"></span>
          </span>
        </li>
      </ul>
    </template>

    <p v-else class="panel__empty">{{ t('results.empty') }}</p>
  </section>
</template>

<style scoped>
.meter__track {
  position: relative;
  height: 0.625rem;
  border-radius: var(--radius-sm);
  background: var(--color-surface-sunken);
}

.meter__fill {
  height: 100%;
  border-radius: var(--radius-sm);
  background: var(--color-accent);
}

/* The target load: a fill that stops short of this line has its headroom. */
.meter__target {
  position: absolute;
  top: -0.25rem;
  bottom: -0.25rem;
  width: 2px;
  margin-left: -1px;
  background: var(--color-text-muted);
}

.meter__caption {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  margin: var(--space-2) 0 0;
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.panel__warning {
  margin: var(--space-4) 0 0;
  padding: var(--space-3);
  border-radius: var(--radius-md);
  background: var(--color-danger-subtle);
  color: var(--color-text);
  font-size: var(--text-sm);
}

.breakdown {
  margin: 0;
  padding: 0;
  list-style: none;
}

.breakdown__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: var(--space-1) var(--space-3);
  padding: var(--space-2) 0;
  border-top: 1px solid var(--color-border);
  font-size: var(--text-sm);
}

.breakdown__bar {
  grid-column: 1 / -1;
  height: 0.25rem;
  border-radius: var(--radius-sm);
  background: var(--color-surface-sunken);
}

.breakdown__fill {
  display: block;
  height: 100%;
  border-radius: var(--radius-sm);
  background: var(--color-accent);
}
</style>
